import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../../core/services/firebase/config';
import { Shield, User as UserIcon, Calendar, Mail } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';

interface UserData {
  uid: string;
  displayName: string;
  email: string;
  handle: string;
  persona: string;
  role: string;
  createdAt: any;
  setupStatus?: string;
}

export const UsersListPage: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useAuth(); // Assuming useAuth provides isAdmin check

  useEffect(() => {
    // Real-time subscription to users collection
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      })) as UserData[];
      
      setUsers(usersData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching users:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    // Handle Firestore Timestamp or Date
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="space-y-6">
       <div className="flex justify-between items-center">
         <div>
           <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Usuários da Plataforma</h1>
           <p className="text-slate-500 dark:text-slate-400">
             Total de {users.length} usuários cadastrados
           </p>
         </div>
       </div>

       <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
          {loading ? (
             <div className="p-12 text-center text-slate-500">
                <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                Carregando usuários...
             </div>
          ) : users.length === 0 ? (
             <div className="p-12 text-center text-slate-500">
                Nenhum usuário encontrado.
             </div>
          ) : (
             <div className="overflow-x-auto">
                <table className="w-full text-left bg-white dark:bg-slate-900">
                   <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                     <tr>
                       <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Usuário</th>
                       <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Contato</th>
                       <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Perfil</th>
                       <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Cadastro</th>
                       <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {users.map(user => (
                        <tr key={user.uid} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                           <td className="p-4">
                              <div className="flex items-center gap-3">
                                 <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">
                                    {user.displayName ? user.displayName[0].toUpperCase() : <UserIcon size={18} />}
                                 </div>
                                 <div>
                                    <p className="font-bold text-slate-900 dark:text-white">{user.displayName || 'Sem nome'}</p>
                                    <p className="text-sm text-slate-500 font-mono">@{user.handle || row_uid_fallback(user.uid)}</p>
                                 </div>
                              </div>
                           </td>
                           <td className="p-4">
                              <div className="text-sm text-slate-600 dark:text-slate-300 flex flex-col gap-1">
                                 <span className="flex items-center gap-2">
                                    <Mail size={14} className="text-slate-400" />
                                    {user.email}
                                 </span>
                              </div>
                           </td>
                           <td className="p-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                 ${user.persona === 'business' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : 
                                   user.persona === 'influencer' ? 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300' :
                                   'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'}
                              `}>
                                 {user.persona || 'user'}
                              </span>
                           </td>
                           <td className="p-4 text-sm text-slate-500 font-mono">
                              {formatDate(user.createdAt)}
                           </td>
                           <td className="p-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                                 ${user.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}
                              `}>
                                 {user.role || 'user'}
                              </span>
                           </td>
                        </tr>
                      ))}
                   </tbody>
                </table>
             </div>
          )}
       </div>
    </div>
  );
};

function row_uid_fallback(uid: string) {
    return uid.substring(0, 8) + '...';
}
